import { useAppSettingHelper } from '@affine/core/components/hooks/affine/use-app-setting-helper';
import { GraphQLService } from '@affine/core/modules/cloud';
import { EditorSettingService } from '@affine/core/modules/editor-setting';
import { I18nService } from '@affine/core/modules/i18n';
import type { GraphQLQuery } from '@affine/graphql';
import type { AppSetting } from '@toeverything/infra';
import { useLiveData, useService } from '@toeverything/infra';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  shouldScheduleUiSettingsVaultSync,
  type UiSettingsVaultChangeSource,
} from './sync-scheduling';

const UI_SETTINGS_VAULT_LOCAL_PREFIX = 'streamlain.uiSettingsVault.local.v1:';
const UI_SETTINGS_VAULT_SYNC_ENABLED_KEY =
  'streamlain.uiSettingsVault.sync.enabled.v1';
const UI_SETTINGS_VAULT_CHANGED_EVENT = 'streamlain:ui-settings-vault-changed';
const UI_SETTINGS_VAULT_LOCAL_STORAGE_APPLY_EVENT =
  'streamlain:ui-settings-vault-local-storage-apply';

export const UI_SETTINGS_VAULT_GLOBAL_SCOPE = 'user.global';
export const UI_SETTINGS_VAULT_LOCAL_STORAGE_SCOPE = 'client.localStorage';

export type UiSettingStorageSyncMode = 'cloud' | 'local-only';

export type UiSettingStorageOptions = {
  /**
   * Use `cloud` for durable preferences users expect across devices: theme,
   * language, editor options, sidebar ordering/sorting.
   *
   * Use `local-only` for device/window state: expanded sections, scroll
   * positions, split widths, pane heights, or any pixel-exact positioning.
   */
  sync: UiSettingStorageSyncMode;
};

type LocalUiSettingsVaultEntry<T = unknown> = {
  scope: string;
  key: string;
  value: T;
  clientUpdatedAt: string;
  syncedClientUpdatedAt?: string;
};

type RemoteUiSettingsVaultEntry = {
  scope: string;
  key: string;
  value: string;
  revision: number;
  clientUpdatedAt: string;
  updatedAt: string;
};

type UiSettingsVaultEntriesResponse = {
  uiSettingsVaultEntries: RemoteUiSettingsVaultEntry[];
};

type PushUiSettingsVaultResponse = {
  pushUiSettingsVault: {
    entries: RemoteUiSettingsVaultEntry[];
  };
};

const uiSettingsVaultEntriesQuery = {
  id: 'uiSettingsVaultEntriesQuery',
  op: 'uiSettingsVaultEntries',
  query: `query uiSettingsVaultEntries($scopes: [String!]) {
  uiSettingsVaultEntries(scopes: $scopes) {
    scope
    key
    value
    revision
    clientUpdatedAt
    updatedAt
  }
}`,
} satisfies GraphQLQuery;

const pushUiSettingsVaultMutation = {
  id: 'pushUiSettingsVaultMutation',
  op: 'pushUiSettingsVault',
  query: `mutation pushUiSettingsVault($input: PushUiSettingsVaultInput!) {
  pushUiSettingsVault(input: $input) {
    entries {
      scope
      key
      value
      revision
      clientUpdatedAt
      updatedAt
    }
  }
}`,
} satisfies GraphQLQuery;

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function encodeStoragePart(value: string): string {
  return encodeURIComponent(value);
}

function vaultStorageKey(scope: string, key: string): string {
  return `${UI_SETTINGS_VAULT_LOCAL_PREFIX}${encodeStoragePart(scope)}:${encodeStoragePart(key)}`;
}

function parseJson<T>(raw: string | null | undefined): T | undefined {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return undefined;
  }
}

function nowIso(): string {
  return new Date().toISOString();
}

/** The time of a value this device only defaulted to: any server entry is newer and wins. */
const DEFAULT_VALUE_UPDATED_AT = new Date(0).toISOString();

function timestamp(value: string | undefined): number {
  if (!value) return 0;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function stableStringify(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function valuesEqual(left: unknown, right: unknown): boolean {
  return stableStringify(left) === stableStringify(right);
}

function dispatchVaultChanged(
  scope: string,
  key: string,
  source: UiSettingsVaultChangeSource = 'local'
): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(UI_SETTINGS_VAULT_CHANGED_EVENT, {
      detail: { scope, key, source },
    })
  );
}

function dispatchLocalStorageApplied(storageKey: string, value: string): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(UI_SETTINGS_VAULT_LOCAL_STORAGE_APPLY_EVENT, {
      detail: { storageKey, value },
    })
  );
}

export function readUiSettingsVaultSyncEnabled(): boolean {
  const storage = getStorage();
  if (!storage) return true;
  return storage.getItem(UI_SETTINGS_VAULT_SYNC_ENABLED_KEY) !== 'false';
}

export function setUiSettingsVaultSyncEnabled(enabled: boolean): void {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(
    UI_SETTINGS_VAULT_SYNC_ENABLED_KEY,
    enabled ? 'true' : 'false'
  );
  window.dispatchEvent(
    new CustomEvent(UI_SETTINGS_VAULT_CHANGED_EVENT, {
      detail: { syncEnabled: enabled },
    })
  );
}

export function useUiSettingsVaultSyncEnabled() {
  const [enabled, setEnabledState] = useState(readUiSettingsVaultSyncEnabled);

  useEffect(() => {
    const refresh = () => setEnabledState(readUiSettingsVaultSyncEnabled());
    window.addEventListener('storage', refresh);
    window.addEventListener(UI_SETTINGS_VAULT_CHANGED_EVENT, refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener(UI_SETTINGS_VAULT_CHANGED_EVENT, refresh);
    };
  }, []);

  const setEnabled = useCallback((nextEnabled: boolean) => {
    setUiSettingsVaultSyncEnabled(nextEnabled);
    setEnabledState(nextEnabled);
  }, []);

  return [enabled, setEnabled] as const;
}

function readLocalUiSettingsVaultValue<T>(
  scope: string,
  key: string
): T | undefined {
  const storage = getStorage();
  if (!storage) return undefined;
  const entry = parseJson<LocalUiSettingsVaultEntry<T>>(
    storage.getItem(vaultStorageKey(scope, key))
  );
  return entry?.value;
}

function readLocalUiSettingsVaultEntry<T>(
  scope: string,
  key: string
): LocalUiSettingsVaultEntry<T> | undefined {
  const storage = getStorage();
  if (!storage) return undefined;
  const entry = parseJson<LocalUiSettingsVaultEntry<T>>(
    storage.getItem(vaultStorageKey(scope, key))
  );
  if (!entry || entry.scope !== scope || entry.key !== key) {
    return undefined;
  }
  return entry;
}

function writeLocalUiSettingsVaultEntry<T>(
  entry: LocalUiSettingsVaultEntry<T>,
  options: { source?: UiSettingsVaultChangeSource } = {}
): void {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(
    vaultStorageKey(entry.scope, entry.key),
    JSON.stringify(entry)
  );
  dispatchVaultChanged(entry.scope, entry.key, options.source);
}

function writeLocalUiSettingsVaultValue<T>(
  scope: string,
  key: string,
  value: T,
  options: { clientUpdatedAt?: string; synced?: boolean } = {}
): void {
  const clientUpdatedAt = options.clientUpdatedAt ?? nowIso();
  writeLocalUiSettingsVaultEntry({
    scope,
    key,
    value,
    clientUpdatedAt,
    syncedClientUpdatedAt: options.synced ? clientUpdatedAt : undefined,
  });
}

function readAllLocalUiSettingsVaultEntries(): LocalUiSettingsVaultEntry[] {
  const storage = getStorage();
  if (!storage) return [];
  const entries: LocalUiSettingsVaultEntry[] = [];
  for (let index = 0; index < storage.length; index++) {
    const storageKey = storage.key(index);
    if (!storageKey?.startsWith(UI_SETTINGS_VAULT_LOCAL_PREFIX)) continue;
    const entry = parseJson<LocalUiSettingsVaultEntry>(
      storage.getItem(storageKey)
    );
    if (entry?.scope && entry.key) {
      entries.push(entry);
    }
  }
  return entries;
}

function readDirtyLocalUiSettingsVaultEntries(): LocalUiSettingsVaultEntry[] {
  return readAllLocalUiSettingsVaultEntries().filter(
    entry => entry.syncedClientUpdatedAt !== entry.clientUpdatedAt
  );
}

function readSyncedLocalStorageSetting(storageKey: string): string | null {
  const mirrored = readLocalUiSettingsVaultValue<string>(
    UI_SETTINGS_VAULT_LOCAL_STORAGE_SCOPE,
    storageKey
  );
  if (typeof mirrored === 'string') return mirrored;
  return getStorage()?.getItem(storageKey) ?? null;
}

function writeSyncedLocalStorageSetting(storageKey: string, rawValue: string) {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(storageKey, rawValue);
  writeLocalUiSettingsVaultValue(
    UI_SETTINGS_VAULT_LOCAL_STORAGE_SCOPE,
    storageKey,
    rawValue
  );
}

function useSyncedLocalStorageSettingListener(
  storageKey: string | null,
  onValue: (rawValue: string) => void
): void {
  useEffect(() => {
    if (!storageKey) return;
    const handler = (event: Event) => {
      const detail = (
        event as CustomEvent<{ storageKey?: string; value?: string }>
      ).detail;
      if (
        detail?.storageKey === storageKey &&
        typeof detail.value === 'string'
      ) {
        onValue(detail.value);
      }
    };
    window.addEventListener(
      UI_SETTINGS_VAULT_LOCAL_STORAGE_APPLY_EVENT,
      handler
    );
    return () => {
      window.removeEventListener(
        UI_SETTINGS_VAULT_LOCAL_STORAGE_APPLY_EVENT,
        handler
      );
    };
  }, [onValue, storageKey]);
}

export function readUiSettingStorageValue(
  storageKey: string,
  options: UiSettingStorageOptions
): string | null {
  if (options.sync === 'cloud') {
    return readSyncedLocalStorageSetting(storageKey);
  }
  return getStorage()?.getItem(storageKey) ?? null;
}

export function writeUiSettingStorageValue(
  storageKey: string,
  rawValue: string,
  options: UiSettingStorageOptions
): void {
  if (options.sync === 'cloud') {
    writeSyncedLocalStorageSetting(storageKey, rawValue);
    return;
  }

  const storage = getStorage();
  if (!storage) return;
  storage.setItem(storageKey, rawValue);
}

export function useUiSettingStorageListener(
  storageKey: string | null,
  options: UiSettingStorageOptions,
  onValue: (rawValue: string) => void
): void {
  useSyncedLocalStorageSettingListener(
    options.sync === 'cloud' ? storageKey : null,
    onValue
  );

  useEffect(() => {
    if (!storageKey || options.sync !== 'local-only') return;
    const handler = (event: StorageEvent) => {
      if (
        event.storageArea === getStorage() &&
        event.key === storageKey &&
        typeof event.newValue === 'string'
      ) {
        onValue(event.newValue);
      }
    };
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('storage', handler);
    };
  }, [onValue, options.sync, storageKey]);
}

function applyLocalStorageMirror(entry: LocalUiSettingsVaultEntry): void {
  if (
    entry.scope !== UI_SETTINGS_VAULT_LOCAL_STORAGE_SCOPE ||
    typeof entry.value !== 'string'
  ) {
    return;
  }
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(entry.key, entry.value);
  dispatchLocalStorageApplied(entry.key, entry.value);
}

async function pullRemoteEntries(
  graphql: GraphQLService
): Promise<RemoteUiSettingsVaultEntry[]> {
  const response = (await graphql.gql({
    query: uiSettingsVaultEntriesQuery,
    variables: {},
    keepNilVariables: false,
  } as any)) as unknown as UiSettingsVaultEntriesResponse;
  return response.uiSettingsVaultEntries;
}

async function pushRemoteEntries(
  graphql: GraphQLService,
  entries: readonly LocalUiSettingsVaultEntry[]
): Promise<RemoteUiSettingsVaultEntry[]> {
  if (!entries.length) return [];
  const response = (await graphql.gql({
    query: pushUiSettingsVaultMutation,
    variables: {
      input: {
        entries: entries.map(entry => ({
          scope: entry.scope,
          key: entry.key,
          value: JSON.stringify(entry.value),
          clientUpdatedAt: entry.clientUpdatedAt,
        })),
      },
    },
  } as any)) as unknown as PushUiSettingsVaultResponse;
  return response.pushUiSettingsVault.entries;
}

function applyRemoteEntry(remote: RemoteUiSettingsVaultEntry): void {
  const value = parseJson<unknown>(remote.value);
  if (value === undefined) return;

  const local = readLocalUiSettingsVaultEntry(remote.scope, remote.key);
  if (
    local &&
    timestamp(local.clientUpdatedAt) > timestamp(remote.clientUpdatedAt)
  ) {
    return;
  }

  if (
    local &&
    local.clientUpdatedAt === remote.clientUpdatedAt &&
    local.syncedClientUpdatedAt === remote.clientUpdatedAt &&
    valuesEqual(local.value, value)
  ) {
    return;
  }

  const entry: LocalUiSettingsVaultEntry = {
    scope: remote.scope,
    key: remote.key,
    value,
    clientUpdatedAt: remote.clientUpdatedAt,
    syncedClientUpdatedAt: remote.clientUpdatedAt,
  };
  writeLocalUiSettingsVaultEntry(entry, { source: 'remote' });
  applyLocalStorageMirror(entry);
}

/**
 * One round with the server: take the newer server entries, then push the
 * local entries that changed. The push answer goes through the same rule as a
 * pull: it never overwrites a local entry that changed while the round was in
 * flight. That entry stays unsynced and goes out with the next round.
 */
export async function syncUiSettingsVaultOnce(
  graphql: GraphQLService
): Promise<void> {
  for (const remote of await pullRemoteEntries(graphql)) {
    applyRemoteEntry(remote);
  }

  const dirtyEntries = readDirtyLocalUiSettingsVaultEntries();
  if (!dirtyEntries.length) return;
  for (const remote of await pushRemoteEntries(graphql, dirtyEntries)) {
    applyRemoteEntry(remote);
  }
}

function useUiSettingsVaultServerSync(graphql: GraphQLService): void {
  const [syncEnabled] = useUiSettingsVaultSyncEnabled();
  const syncingRef = useRef(false);
  const pendingRef = useRef(false);

  const syncNow = useCallback(async () => {
    if (!syncEnabled || syncingRef.current) {
      pendingRef.current = pendingRef.current || syncEnabled;
      return;
    }

    syncingRef.current = true;
    try {
      await syncUiSettingsVaultOnce(graphql);
    } catch {
      // Settings sync is opportunistic; local settings remain authoritative.
    } finally {
      syncingRef.current = false;
      if (pendingRef.current) {
        pendingRef.current = false;
        window.setTimeout(() => void syncNow(), 300);
      }
    }
  }, [graphql, syncEnabled]);

  useEffect(() => {
    if (!syncEnabled) return;
    void syncNow();
  }, [syncEnabled, syncNow]);

  useEffect(() => {
    if (!syncEnabled) return;
    let timer: number | null = null;
    const schedule = (event: Event) => {
      if (!shouldScheduleUiSettingsVaultSync(event)) {
        return;
      }
      if (timer !== null) {
        window.clearTimeout(timer);
      }
      timer = window.setTimeout(() => {
        timer = null;
        void syncNow();
      }, 500);
    };
    window.addEventListener(UI_SETTINGS_VAULT_CHANGED_EVENT, schedule);
    return () => {
      if (timer !== null) window.clearTimeout(timer);
      window.removeEventListener(UI_SETTINGS_VAULT_CHANGED_EVENT, schedule);
    };
  }, [syncEnabled, syncNow]);
}

export function useExternalVaultSetting<T>({
  key,
  scope,
  value,
  onRemoteValue,
}: {
  key: string;
  scope: string;
  value: T;
  onRemoteValue: (value: T) => void;
}) {
  const onRemoteValueRef = useRef(onRemoteValue);
  const hydratedRef = useRef(false);
  const applyingRemoteRef = useRef(false);
  /** The value the local vault entry holds; only a different value is a new choice. */
  const storedValueJsonRef = useRef<string | null>(null);
  const valueRef = useRef(value);
  onRemoteValueRef.current = onRemoteValue;
  valueRef.current = value;

  const applyVaultValue = useCallback((nextValue: T) => {
    storedValueJsonRef.current = stableStringify(nextValue);
    applyingRemoteRef.current = true;
    onRemoteValueRef.current(nextValue);
    window.setTimeout(() => {
      applyingRemoteRef.current = false;
    }, 0);
  }, []);

  useEffect(() => {
    hydratedRef.current = false;
    const local = readLocalUiSettingsVaultEntry<T>(scope, key);
    if (local) {
      storedValueJsonRef.current = stableStringify(local.value);
      if (!valuesEqual(local.value, valueRef.current)) {
        applyVaultValue(local.value);
      }
    } else {
      storedValueJsonRef.current = stableStringify(valueRef.current);
      writeLocalUiSettingsVaultValue(scope, key, valueRef.current, {
        clientUpdatedAt: DEFAULT_VALUE_UPDATED_AT,
      });
    }
    hydratedRef.current = true;
  }, [applyVaultValue, key, scope]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    const valueJson = stableStringify(value);
    if (storedValueJsonRef.current === valueJson) return;
    if (applyingRemoteRef.current) return;
    storedValueJsonRef.current = valueJson;
    writeLocalUiSettingsVaultValue(scope, key, value);
  }, [key, scope, value]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ scope?: string; key?: string }>)
        .detail;
      if (detail?.scope !== scope || detail.key !== key) return;
      const local = readLocalUiSettingsVaultEntry<T>(scope, key);
      if (!local || valuesEqual(local.value, valueRef.current)) return;
      applyVaultValue(local.value);
    };
    window.addEventListener(UI_SETTINGS_VAULT_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(UI_SETTINGS_VAULT_CHANGED_EVENT, handler);
    };
  }, [applyVaultValue, key, scope]);
}

function isAppSettingKey(key: string): key is keyof AppSetting {
  return [
    'clientBorder',
    'windowFrameStyle',
    'enableBlurBackground',
    'enableNoisyBackground',
    'enableAnimations',
    'enableCanvasAnimations',
    'autoCheckUpdate',
    'autoDownloadUpdate',
    'enableTelemetry',
    'showLinkedDocInSidebar',
  ].includes(key);
}

export function useUiSettingsVaultBridge(): void {
  const graphql = useService(GraphQLService);
  const i18n = useService(I18nService).i18n;
  const editorSetting = useService(EditorSettingService).editorSetting;
  const { setTheme, theme } = useTheme();
  const { appSettings, updateSettings } = useAppSettingHelper();
  const currentLanguage = useLiveData(i18n.currentLanguageKey$);
  const editorSettings = useLiveData(editorSetting.settings$);

  useUiSettingsVaultServerSync(graphql);

  const applyTheme = useCallback(
    (value: string) => {
      if (value) {
        setTheme(value);
      }
    },
    [setTheme]
  );

  useExternalVaultSetting({
    scope: UI_SETTINGS_VAULT_GLOBAL_SCOPE,
    key: 'appearance.theme',
    value: theme ?? 'system',
    onRemoteValue: applyTheme,
  });

  const applyLanguage = useCallback(
    (value: string) => {
      if (value) {
        i18n.changeLanguage(value);
      }
    },
    [i18n]
  );

  useExternalVaultSetting({
    scope: UI_SETTINGS_VAULT_GLOBAL_SCOPE,
    key: 'appearance.language',
    value: currentLanguage,
    onRemoteValue: applyLanguage,
  });

  const applyAppSettings = useCallback(
    (value: Partial<AppSetting>) => {
      for (const [key, settingValue] of Object.entries(value)) {
        if (
          (key === 'enableAnimations' || key === 'enableCanvasAnimations') &&
          typeof settingValue !== 'boolean'
        )
          continue;
        if (isAppSettingKey(key)) {
          updateSettings(key, settingValue as AppSetting[typeof key]);
        }
      }
    },
    [updateSettings]
  );

  useExternalVaultSetting({
    scope: UI_SETTINGS_VAULT_GLOBAL_SCOPE,
    key: 'appearance.appSettings',
    value: appSettings,
    onRemoteValue: applyAppSettings,
  });

  const applyEditorSettings = useCallback(
    (value: Record<string, unknown>) => {
      for (const [key, settingValue] of Object.entries(value)) {
        editorSetting.set(key as never, settingValue as never);
      }
    },
    [editorSetting]
  );

  const editorSettingsValue = useMemo(
    () =>
      editorSettings && typeof editorSettings === 'object'
        ? editorSettings
        : {},
    [editorSettings]
  );

  useExternalVaultSetting({
    scope: UI_SETTINGS_VAULT_GLOBAL_SCOPE,
    key: 'editor.settings',
    value: editorSettingsValue,
    onRemoteValue: applyEditorSettings,
  });
}
